import { createContext, useContext, ReactNode } from "react";
import { CreatorsTableContextValue, CreatorsTableStateValue } from "./types";

// Two contexts on purpose: the table's API/scope changes rarely, its UI
// state (open panel, search text, loading, dialogs) changes constantly.
// Cells subscribe to the first and, only where they show it, the second —
// so a keystroke in the search box no longer re-renders every cell.
const CreatorsTableContext = createContext<CreatorsTableContextValue | null>(
  null
);
const CreatorsTableStateContext = createContext<CreatorsTableStateValue | null>(
  null
);

export function useCreatorsTableContext() {
  const context = useContext(CreatorsTableContext);
  if (!context) {
    throw new Error(
      "useCreatorsTableContext must be used within CreatorsTableProvider"
    );
  }
  return context;
}

export function useCreatorsTableContextOptional() {
  return useContext(CreatorsTableContext);
}

/** The table's volatile UI state (see CreatorsTableStateValue). */
export function useCreatorsTableState() {
  const state = useContext(CreatorsTableStateContext);
  if (!state) {
    throw new Error(
      "useCreatorsTableState must be used within CreatorsTableProvider"
    );
  }
  return state;
}

export function CreatorsTableProvider({
  children,
  value,
  state,
}: {
  children: ReactNode;
  value: CreatorsTableContextValue;
  state: CreatorsTableStateValue;
}) {
  return (
    <CreatorsTableContext.Provider value={value}>
      <CreatorsTableStateContext.Provider value={state}>
        {children}
      </CreatorsTableStateContext.Provider>
    </CreatorsTableContext.Provider>
  );
}
